import { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/utils/api';
import { EyeIcon } from '@/ui/icons';

const BASE_ORIGIN = 'https://mun.esdfl.net';

type Announcement = {
  id: number;
  title: string;
  slug: string;
  contentHtml: string;
  excerpt?: string;
  publishedAt?: string;
  viewCount?: number;
};

export function AnnouncementPage() {
  const { slug } = useParams<{ slug: string }>();
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(true);

  // SEO meta
  const meta = useMemo(() => {
    if (!announcement) return null;
    const plain = announcement.contentHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const desc = announcement.excerpt || plain.slice(0, 160);
    return {
      title: `${announcement.title} | Duyurular | ESDFL MUN`,
      description: desc,
    };
  }, [announcement]);

  const annUrl = useMemo(() => announcement ? `${BASE_ORIGIN}/duyuru/${announcement.slug}` : '', [announcement]);

  // JSON-LD Article
  const jsonLdArticle = useMemo(() => {
    if (!announcement) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: announcement.title,
      description: meta?.description,
      datePublished: announcement.publishedAt,
      dateModified: announcement.publishedAt,
      url: annUrl,
      mainEntityOfPage: annUrl,
      author: { '@type': 'Organization', name: 'Edirne Süleyman Demirel Fen Lisesi MUN' },
      publisher: {
        '@type': 'Organization',
        name: 'Edirne Süleyman Demirel Fen Lisesi MUN',
        logo: { '@type': 'ImageObject', url: `${BASE_ORIGIN}/mun_logo.png` }
      }
    };
  }, [announcement, annUrl, meta]);

  // JSON-LD Breadcrumb
  const jsonLdBreadcrumb = useMemo(() => {
    if (!announcement) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: `${BASE_ORIGIN}/` },
        { '@type': 'ListItem', position: 2, name: 'Duyurular', item: `${BASE_ORIGIN}/duyurular` },
        { '@type': 'ListItem', position: 3, name: announcement.title, item: annUrl }
      ]
    };
  }, [announcement, annUrl]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await api.get(`/api/public/announcements/${slug}`);
        if (!ignore) {
          setAnnouncement(res.announcement ?? null);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    // Track view
    (async () => {
      try {
        const res = await api.post(`/api/public/announcement_view/${slug}`, {});
        if (!ignore && res.newCount !== undefined) {
          setAnnouncement((a) => a ? { ...a, viewCount: res.newCount } : a);
        }
      } catch {}
    })();
    return () => { ignore = true; };
  }, [slug]);

  if (loading) {
    return <div>Yükleniyor…</div>;
  }

  if (!announcement) {
    return (
      <div className="card p-8 text-center">
        <h1 className="text-xl font-semibold mb-2">Bulunamadı</h1>
        <p className="text-gray-600 mb-4">Bu duyuru bulunamadı.</p>
        <Link to="/duyurular" className="btn">Duyurulara Dön</Link>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm transition-colors duration-300">
      {meta && (
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
          <meta property="og:title" content={meta.title} />
          <meta property="og:description" content={meta.description} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={annUrl} />
          <meta property="og:image" content={`${BASE_ORIGIN}/mun_logo.png`} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content={meta.title} />
          <meta name="twitter:description" content={meta.description} />
          <link rel="canonical" href={annUrl} />
          {jsonLdArticle && <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>}
          {jsonLdBreadcrumb && <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>}
        </Helmet>
      )}

      <Link to="/duyurular" className="text-brand-600 hover:text-brand-700 mb-4 inline-block">
        ← Duyurulara Dön
      </Link>

      <article className="card p-6">
        <h1 className="text-2xl md:text-3xl font-serif font-bold mb-2 text-gray-900 dark:text-white">{announcement.title}</h1>
        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mb-6">
          {announcement.publishedAt && (
            <span>{new Date(announcement.publishedAt).toLocaleDateString('tr-TR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</span>
          )}
          <span className="inline-flex items-center gap-1"><EyeIcon /> {announcement.viewCount ?? 0}</span>
        </div>
        <div 
          className="prose dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: announcement.contentHtml }}
        />
      </article>
    </div>
  );
}
